// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/** The LOCAL, in-repo multi-INT dataset the dogfood capability bundles + publishes.
 *
 *  PURITY NOTE (the exit gate): this module is the capability's OWN bundled data. It
 *  imports ONLY the public-SDK envelope types (@tak-tdf/contracts) — NO host internal,
 *  NO abac-pdp, NO pdp.ts. The dogfood capability (multiIntCapability.ts) imports this
 *  file, which is explicitly whitelisted by the SDK-purity scanner as "its own dataset".
 *
 *  A compact slice curated from /Users/will/dev/TDFBot-Dataset-Intelligence/multi-int/*
 *  (SIGINT / HUMINT / GEOINT / ELINT / OSINT), theater-consistent with the live Hormuz
 *  picture (~26.6N 56.6E) so it sits WITH the running COP. Classifications are VARIED
 *  across the full lattice (UNCLASSIFIED → TOP_SECRET) so a low-clearance persona sees a
 *  STRICT SUBSET of what a high-clearance persona sees from the SAME dataset — the
 *  clearance-shaping the data + render seams enforce, observed end-to-end.
 *
 *  These are rendered as `kind: "sensor"` (the multi-INT observation kind) so the
 *  capability's own layer does NOT fight tracksCapability (track/alert/observation). */

import type { CanonicalEnvelope } from "../../../../packages/contracts-ts/src/index.ts";

/** The envelope kind the multi-INT capability renders (distinct from tracksCapability,
 *  which draws track/alert/observation). Exported so the capability + the gate agree. */
export const MULTI_INT_KIND = "sensor" as const;

/** A compact authoring shape; expanded to a full CanonicalEnvelope by `toEnvelope`. */
interface IntReport {
  id: string;
  discipline: "SIGINT" | "HUMINT" | "GEOINT" | "ELINT" | "OSINT";
  name: string;
  lat: number;
  lon: number;
  classification: "UNCLASSIFIED" | "CONFIDENTIAL" | "SECRET" | "TOP_SECRET";
  releasability: string[];
  mission: string[];
  caveats: string[];
}

/** The curated multi-INT slice. Spread across the lattice on purpose:
 *  UNCLASSIFIED (OSINT) → CONFIDENTIAL (GEOINT) → SECRET (SIGINT/ELINT/HUMINT) →
 *  TOP_SECRET (the crown-jewel SIGINT/HUMINT reports). Releasability stays within the
 *  SEED bundle's `releasable_to` value set (USA/GBR/CAN/AUS/NZL). */
const REPORTS: IntReport[] = [
  // --- UNCLASSIFIED (OSINT) — every persona sees these ---
  {
    id: "scop:int:osint-ais-tanker",
    discipline: "OSINT",
    name: "OSINT · AIS plot — VLCC loitering near Larak anchorage",
    lat: 26.86, lon: 56.37,
    classification: "UNCLASSIFIED",
    releasability: ["USA", "GBR", "CAN", "AUS", "NZL"],
    mission: [], caveats: [],
  },
  {
    id: "scop:int:osint-portcall",
    discipline: "OSINT",
    name: "OSINT · Open port-call register — Bandar Abbas berth turnover",
    lat: 27.13, lon: 56.21,
    classification: "UNCLASSIFIED",
    releasability: ["USA", "GBR", "CAN", "AUS", "NZL"],
    mission: [], caveats: [],
  },
  // --- CONFIDENTIAL (GEOINT) ---
  {
    id: "scop:int:geoint-imagery-staging",
    discipline: "GEOINT",
    name: "GEOINT · EO imagery — small-craft staging, Larak Island",
    lat: 26.85, lon: 56.36,
    classification: "CONFIDENTIAL",
    releasability: ["USA", "GBR", "CAN", "AUS", "NZL"],
    mission: ["STEEL_HORIZON"], caveats: [],
  },
  // --- SECRET (SIGINT / ELINT / HUMINT) ---
  {
    id: "scop:int:sigint-vhf-net",
    discipline: "SIGINT",
    name: "SIGINT · IRGC-N VHF tactical net re-keyed (sortie-cycle reset)",
    lat: 27.15, lon: 56.21,
    classification: "SECRET",
    releasability: ["USA", "GBR", "CAN", "AUS", "NZL"],
    mission: ["STEEL_HORIZON"], caveats: [],
  },
  {
    id: "scop:int:elint-fcr-emitter",
    discipline: "ELINT",
    name: "ELINT · Coastal fire-control radar emitter — intermittent radiate",
    lat: 26.95, lon: 56.31,
    classification: "SECRET",
    releasability: ["USA", "GBR", "CAN", "AUS", "NZL"],
    mission: ["STEEL_HORIZON"], caveats: [],
  },
  {
    id: "scop:int:humint-source-larak",
    discipline: "HUMINT",
    name: "HUMINT · Source report — crane time requested, Larak load-out",
    lat: 26.862, lon: 56.363,
    classification: "SECRET",
    releasability: ["USA", "GBR", "CAN", "AUS", "NZL"],
    mission: ["STEEL_HORIZON"], caveats: [],
  },
  // --- TOP_SECRET (the crown jewels — only the top persona sees these) ---
  {
    id: "scop:int:sigint-mine-loadout",
    discipline: "SIGINT",
    name: "SIGINT · COMINT gist — 'heavy stores' / mine load-out tasking",
    lat: 26.86, lon: 56.35,
    classification: "TOP_SECRET",
    releasability: ["USA"],
    mission: ["STEEL_HORIZON"], caveats: [],
  },
  {
    id: "scop:int:humint-intent-mining",
    discipline: "HUMINT",
    name: "HUMINT · Vetted source — directed intent to mine the central Strait",
    lat: 26.70, lon: 56.50,
    classification: "TOP_SECRET",
    releasability: ["USA"],
    mission: ["STEEL_HORIZON"], caveats: [],
  },
];

/** Expand an authoring report into a full CanonicalEnvelope. Pure; deterministic
 *  timestamps so re-publishing is idempotent (same id ⇒ engine update, not duplicate). */
function toEnvelope(r: IntReport): CanonicalEnvelope {
  const t = "2026-04-11T08:20:00.000Z";
  return {
    id: r.id,
    schema: "scop.envelope/1.0",
    kind: MULTI_INT_KIND,
    time: t,
    valid_time: { start: t, stale: "2026-04-12T08:20:00.000Z" },
    geometry: { type: "Point", coordinates: [r.lon, r.lat] },
    properties: {
      name: r.name,
      affiliation: "unknown",
      type: "multi-int",
      source: `int:${r.discipline.toLowerCase()}`,
      int_discipline: r.discipline,
    },
    security: {
      classification: r.classification,
      releasability: r.releasability,
      mission: r.mission,
      caveats: r.caveats,
      originator_control: false,
      policy_ref: "policy://defense.local",
      payload_mode: "clear",
      leakage_profile: "clear",
    },
    provenance: {
      originator: "multi-int-capability",
      adapter: "bundled-dataset",
      received_at: t,
      chain: [{ adapter: "bundled-dataset", at: t }],
    },
    payload: { clear: { discipline: r.discipline, summary: r.name } },
  } as CanonicalEnvelope;
}

/** The bundled multi-INT dataset as canonical envelopes. */
export const MULTI_INT_DATASET: CanonicalEnvelope[] = REPORTS.map(toEnvelope);
